// One improvement request: the screenshots, the details, the conversation,
// and -- for the person who asked -- Approve / Request another change.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiErrorMessage } from "../../api";
import { fileToDataUrl, imageFromPaste } from "../../components/feedback/captureScreen";
import {
  CATEGORIES, PRIORITIES, STATUSES, addFeedbackComment, addFeedbackScreenshot,
  approveFeedback, deleteFeedback, getFeedback, lookup, requestFeedbackChange,
  screenshotUrl, updateFeedback,
} from "../../components/feedback/feedbackApi";
import { CategoryChip, PriorityFlag } from "./ImprovementsPage";

const box = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 16 };
const select = { width: "100%", padding: "6px 8px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14 };
const primary = { background: "#4f46e5", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontWeight: 600, cursor: "pointer" };
const secondary = { background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 8, padding: "8px 14px", cursor: "pointer" };

const fmt = (iso) => new Date(iso).toLocaleString(undefined, {
  month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
});

function StatusPill({ value }) {
  const s = lookup(STATUSES, value);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: s.color }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: s.color }} />{s.label}
    </span>
  );
}

export default function ImprovementDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [error, setError] = useState("");
  const [activeShot, setActiveShot] = useState(null);
  const [comment, setComment] = useState("");
  const [changeNote, setChangeNote] = useState("");
  const [askingChange, setAskingChange] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await getFeedback(id);
      setItem(data);
      setError("");
      setActiveShot((cur) =>
        data.screenshots.some((s) => s.id === cur) ? cur : data.screenshots[data.screenshots.length - 1]?.id ?? null);
    } catch (e) {
      setError(apiErrorMessage(e, "Could not load this request."));
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const run = async (fn, fallback) => {
    setBusy(true);
    setError("");
    try {
      const result = await fn();
      if (result && result.id && result.comments) setItem(result);
      else await load();
      return true;
    } catch (e) {
      setError(apiErrorMessage(e, fallback));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const addUpdatedShot = async (file) => {
    if (!file) return;
    let image;
    try {
      image = await fileToDataUrl(file);
    } catch (e) {
      setError(e.message);
      return;
    }
    await run(() => addFeedbackScreenshot(id, image, item.can_review ? "RESOLUTION" : "ORIGINAL"),
      "Could not add that screenshot.");
    window.dispatchEvent(new CustomEvent("feedback-changed"));
  };

  // A screenshot pasted on this page attaches to the request.
  useEffect(() => {
    const onPaste = (e) => {
      if (!item || !(item.can_review || item.is_submitter)) return;
      if (["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) return;
      const file = imageFromPaste(e);
      if (file) { e.preventDefault(); addUpdatedShot(file); }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  });

  if (!item) {
    return <div style={{ padding: 24, color: error ? "#b91c1c" : "#6b7280" }}>{error || "Loading…"}</div>;
  }

  const canEditDetails = item.can_review || (item.is_submitter && item.status !== "RESOLVED");
  const waitingOnMe = item.is_submitter && item.status === "READY_FOR_REVIEW";
  const resolutionShot = [...item.screenshots].reverse().find((s) => s.kind === "RESOLUTION");

  return (
    <div style={{ padding: 24 }}>
      <Link to="/improvements" style={{ color: "#4f46e5", fontSize: 13, textDecoration: "none" }}>← App Improvements</Link>
      <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "8px 0 16px", flexWrap: "wrap" }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>#{item.id} {item.title}</h1>
        <CategoryChip value={item.category} />
        <StatusPill value={item.status} />
      </div>

      {waitingOnMe && (
        <div data-testid="signoff-banner" style={{ ...box, borderColor: "#c4b5fd", background: "#f5f3ff", marginBottom: 16 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>This has been changed. Does it look right?</div>
          <div style={{ fontSize: 13, color: "#4b5563", marginBottom: 10 }}>
            {resolutionShot ? "The updated screen is shown below. " : ""}
            {item.page_url && <>You can also <a href={item.page_url} style={{ color: "#4f46e5" }}>open the page</a> and try it. </>}
          </div>
          {askingChange ? (
            <div style={{ display: "grid", gap: 8 }}>
              <textarea value={changeNote} onChange={(e) => setChangeNote(e.target.value)} autoFocus
                placeholder="What still needs to change?" aria-label="What still needs to change?"
                style={{ ...select, minHeight: 70, fontFamily: "inherit" }} />
              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" style={primary} disabled={busy || !changeNote.trim()}
                  onClick={async () => {
                    if (await run(() => requestFeedbackChange(id, changeNote.trim()), "Could not send that.")) {
                      setAskingChange(false); setChangeNote("");
                    }
                  }}>Send back</button>
                <button type="button" style={secondary} onClick={() => setAskingChange(false)}>Cancel</button>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" disabled={busy}
                style={{ ...primary, background: "#16a34a" }}
                onClick={() => run(() => approveFeedback(id), "Could not approve.")}>
                ✓ Approve
              </button>
              <button type="button" style={secondary} disabled={busy} onClick={() => setAskingChange(true)}>
                Request another change
              </button>
            </div>
          )}
        </div>
      )}

      {error && <div role="alert" style={{ color: "#b91c1c", marginBottom: 12 }}>{error}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 340px", gap: 16, alignItems: "start" }}>
        <div style={{ display: "grid", gap: 16 }}>
          <div style={box}>
            {activeShot ? (
              <a href={screenshotUrl(activeShot)} target="_blank" rel="noreferrer">
                <img src={screenshotUrl(activeShot)} alt="Screenshot"
                  style={{ width: "100%", borderRadius: 8, border: "1px solid #f3f4f6", display: "block" }} />
              </a>
            ) : (
              <div style={{ color: "#9ca3af", padding: 40, textAlign: "center" }}>No screenshot</div>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap", alignItems: "center" }}>
              {item.screenshots.map((s) => (
                <button key={s.id} type="button" onClick={() => setActiveShot(s.id)}
                  style={{
                    padding: 0, border: s.id === activeShot ? "2px solid #4f46e5" : "1px solid #e5e7eb",
                    borderRadius: 6, background: "#fff", cursor: "pointer", position: "relative",
                  }}>
                  <img src={screenshotUrl(s.id)} alt="" style={{ width: 96, height: 60, objectFit: "cover", objectPosition: "top", borderRadius: 5, display: "block" }} />
                  <span style={{ position: "absolute", left: 4, bottom: 4, fontSize: 10, background: s.kind === "RESOLUTION" ? "#16a34a" : "#374151", color: "#fff", borderRadius: 4, padding: "0 4px" }}>
                    {s.kind === "RESOLUTION" ? "Updated" : "Before"}
                  </span>
                </button>
              ))}
              {(item.can_review || item.is_submitter) && (
                <>
                  <button type="button" style={{ ...secondary, fontSize: 12, padding: "6px 10px" }}
                    onClick={() => fileRef.current?.click()} disabled={busy}>
                    + {item.can_review ? "Add updated screenshot" : "Add screenshot"}
                  </button>
                  <span style={{ fontSize: 11, color: "#9ca3af" }}>or paste one (⌘V)</span>
                  <input ref={fileRef} type="file" accept="image/*" hidden
                    onChange={(e) => { addUpdatedShot(e.target.files?.[0]); e.target.value = ""; }} />
                </>
              )}
            </div>
          </div>

          <div style={box}>
            <div style={{ fontWeight: 700, marginBottom: 12 }}>Discussion</div>
            <div style={{ display: "grid", gap: 12, marginBottom: 12 }}>
              {item.description && (
                <div>
                  <div style={{ fontSize: 12, color: "#6b7280" }}><strong style={{ color: "#111827" }}>{item.submitted_by_name}</strong> · {fmt(item.created_at)}</div>
                  <div style={{ whiteSpace: "pre-wrap", fontSize: 14, marginTop: 2 }}>{item.description}</div>
                </div>
              )}
              {item.comments.map((c) => c.kind === "EVENT" ? (
                <div key={c.id} style={{ fontSize: 12, color: "#6b7280", fontStyle: "italic" }}>
                  {c.author_name} · {c.body} · {fmt(c.created_at)}
                </div>
              ) : (
                <div key={c.id}>
                  <div style={{ fontSize: 12, color: "#6b7280" }}><strong style={{ color: "#111827" }}>{c.author_name}</strong> · {fmt(c.created_at)}</div>
                  <div style={{ whiteSpace: "pre-wrap", fontSize: 14, marginTop: 2 }}>{c.body}</div>
                </div>
              ))}
              {!item.description && !item.comments.length && (
                <div style={{ color: "#9ca3af", fontSize: 13 }}>No messages yet.</div>
              )}
            </div>
            <form onSubmit={async (e) => {
              e.preventDefault();
              if (comment.trim() && await run(() => addFeedbackComment(id, comment.trim()), "Could not post that.")) setComment("");
            }} style={{ display: "flex", gap: 8 }}>
              <textarea value={comment} onChange={(e) => setComment(e.target.value)}
                placeholder="Write a reply…" aria-label="Reply"
                onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) e.currentTarget.form.requestSubmit(); }}
                style={{ ...select, minHeight: 44, fontFamily: "inherit", flex: 1 }} />
              <button type="submit" style={primary} disabled={busy || !comment.trim()}>Send</button>
            </form>
          </div>
        </div>

        <div style={{ ...box, display: "grid", gap: 14 }}>
          <div style={{ fontWeight: 700 }}>Details</div>
          <label style={{ fontSize: 12, color: "#6b7280" }}>Status
            {item.can_review ? (
              <select style={select} value={item.status} disabled={busy}
                onChange={(e) => run(() => updateFeedback(id, { status: e.target.value }), "Could not change status.")}>
                {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            ) : <div><StatusPill value={item.status} /></div>}
          </label>
          <label style={{ fontSize: 12, color: "#6b7280" }}>Priority
            {canEditDetails ? (
              <select style={select} value={item.priority} disabled={busy}
                onChange={(e) => run(() => updateFeedback(id, { priority: e.target.value }), "Could not change priority.")}>
                {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            ) : <div><PriorityFlag value={item.priority} /></div>}
          </label>
          <label style={{ fontSize: 12, color: "#6b7280" }}>Type
            {canEditDetails ? (
              <select style={select} value={item.category} disabled={busy}
                onChange={(e) => run(() => updateFeedback(id, { category: e.target.value }), "Could not change type.")}>
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            ) : <div><CategoryChip value={item.category} /></div>}
          </label>
          <div style={{ fontSize: 12, color: "#6b7280" }}>Submitted by
            <div style={{ color: "#111827", fontSize: 14 }}>{item.submitted_by_name} · {fmt(item.created_at)}</div>
          </div>
          {item.page_url && (
            <div style={{ fontSize: 12, color: "#6b7280" }}>Page
              <div><Link to={item.page_url} style={{ color: "#4f46e5", fontSize: 14, wordBreak: "break-all" }}>{item.page_url}</Link></div>
            </div>
          )}
          {item.approved_at && (
            <div style={{ fontSize: 12, color: "#16a34a" }}>Approved {fmt(item.approved_at)}</div>
          )}
          {item.can_review && item.status !== "READY_FOR_REVIEW" && item.status !== "RESOLVED" && (
            <button type="button" style={{ ...primary, background: "#7c3aed" }} disabled={busy}
              onClick={() => run(() => updateFeedback(id, { status: "READY_FOR_REVIEW" }), "Could not change status.")}>
              Send to {item.submitted_by_name} for approval
            </button>
          )}
          {(item.can_review || (item.is_submitter && item.status === "SUBMITTED")) && (
            <button type="button" style={{ ...secondary, color: "#b91c1c", borderColor: "#fecaca" }} disabled={busy}
              onClick={async () => {
                if (!window.confirm("Delete this request and its conversation?")) return;
                try {
                  await deleteFeedback(id);
                  navigate("/improvements");
                } catch (e) {
                  setError(apiErrorMessage(e, "Could not delete."));
                }
              }}>
              Delete request
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
